import {
  CreateResponsibilityInput,
  DeleteResponsibilityInput,
} from '../queries';
import { RoleId } from '../../../insights/types/RoleTypes';
import {
  getEnhancedDataLeads,
  getCurrentDataLeads,
  normalizeLogin,
} from './dataLeadUtils';
import { FunctionalArea, ChangesSummaryItem, Application } from '../types';
import { ASSIGNMENT_MODES } from '../constants/assignmentModes';

// Intermediate type for delete inputs before conversion to responsibilityId format
interface DeleteInputWithIdentity {
  applicationId: number;
  identityId: string;
  identityType: 'USER';
  roleId: number;
}

/**
 * Generate mutation inputs based on data lead assignment changes
 *
 * IMPORTANT LOGIC:
 * - Applications can transition between Default and Custom modes
 * - Custom→Default: DELETE all existing explicit responsibilities to restore functional area default
 * - Default→Custom: CREATE explicit responsibilities for selected subset
 * - Custom→Custom: Normal add/remove logic for assignment changes
 * - Applications with no existing responsibilities default to showing all functional area data leads as selected
 *
 * @param changes - Array of changes summary items
 * @param functionalAreas - Array of functional areas
 * @param selectedFunctionalArea - Name of the selected functional area
 * @param applications - Array of applications to check original state
 * @param currentAssignments - Map of current assignments to know final state
 * @returns Object containing create and delete mutation inputs
 */
export const generateMutationInputs = (
  changes: ChangesSummaryItem[],
  functionalAreas: FunctionalArea[] | undefined,
  selectedFunctionalArea: string,
  applications: Application[], // Add applications parameter to check original state
  currentAssignments: Map<number, Set<string>>, // Add current assignments to know final state
) => {
  const createInputs: CreateResponsibilityInput[] = [];
  const deleteInputs: DeleteInputWithIdentity[] = [];

  // Get enhanced data leads to map login to identityId
  const functionalAreaDataLeads = getEnhancedDataLeads(
    functionalAreas,
    selectedFunctionalArea,
  );

  changes.forEach(change => {
    // Find the application to check its original state
    const app = applications.find(
      a => a.applicationId === change.applicationId,
    );
    if (!app) return;

    // Check if this application originally had any data lead responsibilities
    const originalDataLeads = getCurrentDataLeads(app);
    const hadOriginalResponsibilities = originalDataLeads.length > 0;
    const originalMode = hadOriginalResponsibilities
      ? ASSIGNMENT_MODES.CUSTOM
      : ASSIGNMENT_MODES.DEFAULT;

    // Get current assignments and determine current mode
    const currentAppAssignments =
      currentAssignments.get(change.applicationId) || new Set();

    // Check if current assignments match ALL functional area data leads (Default mode)
    const allFunctionalAreaLogins = new Set(
      functionalAreaDataLeads
        .filter(lead => lead.login)
        .map(lead => normalizeLogin(lead.login)),
    );

    const currentNormalizedAssignments = new Set(
      Array.from(currentAppAssignments).map(login => normalizeLogin(login)),
    );

    const isCurrentlyDefaultMode =
      allFunctionalAreaLogins.size > 0 &&
      allFunctionalAreaLogins.size === currentNormalizedAssignments.size &&
      Array.from(allFunctionalAreaLogins).every(login =>
        currentNormalizedAssignments.has(login),
      );

    const currentMode = isCurrentlyDefaultMode
      ? ASSIGNMENT_MODES.DEFAULT
      : ASSIGNMENT_MODES.CUSTOM;

    // Handle different transition scenarios
    if (
      originalMode === ASSIGNMENT_MODES.DEFAULT &&
      currentMode === ASSIGNMENT_MODES.DEFAULT
    ) {
      // Default → Default: No mutations needed (functional area default applies)
    } else if (
      originalMode === ASSIGNMENT_MODES.DEFAULT &&
      currentMode === ASSIGNMENT_MODES.CUSTOM
    ) {
      // Default → Custom: CREATE explicit responsibilities for selected subset
      currentAppAssignments.forEach(leadLogin => {
        const dataLead = functionalAreaDataLeads.find(
          lead => normalizeLogin(lead.login) === normalizeLogin(leadLogin),
        );
        if (dataLead) {
          createInputs.push({
            applicationId: change.applicationId,
            identityId: dataLead.login,
            identityType: 'USER',
            roleId: RoleId.DataLead, // 205
          });
        }
      });
    } else if (
      originalMode === ASSIGNMENT_MODES.CUSTOM &&
      currentMode === ASSIGNMENT_MODES.DEFAULT
    ) {
      // Custom → Default: DELETE all existing explicit responsibilities
      originalDataLeads.forEach(originalLead => {
        if (originalLead.login) {
          deleteInputs.push({
            applicationId: change.applicationId,
            identityId: normalizeLogin(originalLead.login),
            identityType: 'USER',
            roleId: RoleId.DataLead, // 205
          });
        }
      });
    } else if (
      originalMode === ASSIGNMENT_MODES.CUSTOM &&
      currentMode === ASSIGNMENT_MODES.CUSTOM
    ) {
      // Custom → Custom: Normal add/remove logic for assignment changes
      change.changes.forEach(changeDetail => {
        const dataLead = functionalAreaDataLeads.find(
          lead =>
            normalizeLogin(lead.login) ===
            normalizeLogin(changeDetail.leadLogin),
        );
        if (dataLead) {
          if (changeDetail.action === 'added') {
            createInputs.push({
              applicationId: change.applicationId,
              identityId: dataLead.login,
              identityType: 'USER',
              roleId: RoleId.DataLead, // 205
            });
          } else if (changeDetail.action === 'removed') {
            deleteInputs.push({
              applicationId: change.applicationId,
              identityId: dataLead.login,
              identityType: 'USER',
              roleId: RoleId.DataLead, // 205
            });
          }
        }
      });
    }
  });

  return {
    createInputs,
    deleteInputs,
  };
};

/**
 * Log the mutations that would be executed
 * @param changes - Array of changes summary items
 * @param functionalAreas - Available functional areas
 * @param selectedFunctionalArea - Currently selected functional area
 * @param applications - Available applications
 * @param currentAssignments - Current data lead assignments per application
 * @returns Object containing create and delete mutation inputs
 */
export const logMutationDetails = (
  changes: ChangesSummaryItem[],
  functionalAreas: FunctionalArea[] | undefined,
  selectedFunctionalArea: string,
  applications: Application[],
  currentAssignments: Map<number, Set<string>>,
) => {
  const { createInputs, deleteInputs } = generateMutationInputs(
    changes,
    functionalAreas,
    selectedFunctionalArea,
    applications,
    currentAssignments,
  );

  return {
    createInputs,
    deleteInputs,
  };
};

/**
 * Convert delete inputs with identity info to single delete inputs with responsibility IDs
 * @param deleteInputs - Array of delete inputs from generateMutationInputs
 * @param applications - Array of applications to find responsibilityIds
 * @returns Object containing delete inputs and a mapping from responsibility ID to application ID
 */
export const generateSingleDeleteInputs = (
  deleteInputs: DeleteInputWithIdentity[], // Original delete inputs with identityId, roleId, etc.
  applications: Application[],
): {
  deleteInputs: DeleteResponsibilityInput[];
  responsibilityToAppMap: Map<number, number>;
} => {
  const singleDeleteInputs: DeleteResponsibilityInput[] = [];
  const responsibilityToAppMap = new Map<number, number>();

  deleteInputs.forEach(deleteInput => {
    const app = applications.find(
      a => a.applicationId === deleteInput.applicationId,
    );
    if (!app) {
      return;
    }

    let foundMatch = false;

    // Find all responsibilities for this app and role
    app.roles.forEach(role => {
      role.responsibilities.forEach(responsibility => {
        const roleMatches = responsibility.roleId === deleteInput.roleId;
        const loginMatches =
          normalizeLogin(responsibility.responsibleParty.login) ===
          normalizeLogin(deleteInput.identityId);
        const hasResponsibilityId =
          responsibility.responsibilityId !== undefined;

        if (
          roleMatches &&
          loginMatches &&
          hasResponsibilityId &&
          responsibility.responsibilityId
        ) {
          singleDeleteInputs.push({
            responsibilityId: responsibility.responsibilityId,
            applicationId: deleteInput.applicationId,
          });
          responsibilityToAppMap.set(
            responsibility.responsibilityId,
            deleteInput.applicationId,
          );
          foundMatch = true;
        }
      });
    });

    if (!foundMatch) {
      // No matching responsibility found for deletion - could be logged to monitoring service
    }
  });

  return {
    deleteInputs: singleDeleteInputs,
    responsibilityToAppMap,
  };
};
